// traceability: requirements: [FR-002]
import { api } from '@/hooks/useApi';
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  MfaSetupResponse,
  MfaVerifyRequest,
  RefreshTokenRequest,
} from '@/types/api';
import type { AuthTokens, OAuthProvider, User } from '@/types/models';

interface BackendTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  requires_mfa?: string;
  user_id?: string;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return {};
  }
}

function mapTokenResponse(raw: BackendTokenResponse, email?: string): LoginResponse {
  if (raw.requires_mfa) {
    return { mfaRequired: true } as LoginResponse;
  }

  const payload = decodeJwtPayload(raw.access_token);

  const tokens: AuthTokens = {
    accessToken: raw.access_token,
    refreshToken: raw.refresh_token,
    expiresIn: typeof payload.exp === 'number' && typeof payload.iat === 'number'
      ? payload.exp - payload.iat
      : 3600,
    tokenType: 'Bearer',
  };

  const user: User = {
    id: (payload.sub as string) ?? '',
    email: email ?? '',
    firstName: '',
    lastName: '',
    role: (payload.role as User['role']) ?? 'client',
    language: 'en',
    avatarUrl: null,
    mfaEnabled: false,
    emailVerified: false,
    consentMarketing: false,
    consentDataProcessing: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  return { tokens, user };
}

export async function loginApi(data: LoginRequest): Promise<LoginResponse> {
  const response = await api.post<BackendTokenResponse>('/auth/login', data);
  return mapTokenResponse(response.data, data.email);
}

export async function registerApi(data: RegisterRequest): Promise<RegisterResponse> {
  const response = await api.post<BackendTokenResponse>('/auth/register', data);
  return mapTokenResponse(response.data, data.email) as RegisterResponse;
}

export async function getOAuthUrl(provider: OAuthProvider): Promise<string> {
  const response = await api.get<{ url: string; provider?: string }>(`/auth/oauth/${provider}`);
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('oauth_provider', provider);
  }
  return response.data.url;
}

export async function oauthDevLogin(provider: OAuthProvider, email: string): Promise<LoginResponse> {
  const response = await api.post<BackendTokenResponse>('/auth/oauth/dev-login', { provider, email });
  return mapTokenResponse(response.data, email);
}

/** Complete real OAuth after provider redirect. Requires provider client secrets on the backend. */
export async function oauthCallback(
  provider: string,
  code: string,
  redirectUri: string,
): Promise<LoginResponse> {
  const response = await api.post<BackendTokenResponse>('/auth/oauth/callback', {
    provider,
    code,
    redirect_uri: redirectUri,
  });
  return mapTokenResponse(response.data);
}

export async function refreshTokenApi(data: RefreshTokenRequest): Promise<LoginResponse> {
  const response = await api.post<BackendTokenResponse>('/auth/refresh', {
    refresh_token: data.refreshToken,
  });
  return mapTokenResponse(response.data);
}

export async function getMeApi(): Promise<User> {
  const response = await api.get<{ user: User }>('/auth/me');
  const user = response.data.user;
  if (user.avatarUrl) {
    user.avatarUrl = `${user.avatarUrl}?t=${Date.now()}`;
  }
  return user;
}

export async function setupMfa(): Promise<MfaSetupResponse> {
  const response = await api.post<MfaSetupResponse>('/auth/mfa/setup');
  return response.data;
}

export async function verifyMfa(data: MfaVerifyRequest): Promise<{ success: boolean; recoveryCodes?: string[] }> {
  const { useAuthStore } = await import('@/stores/authStore');
  const userId = useAuthStore.getState().user?.id ?? '';
  const response = await api.post<{ success: boolean; recoveryCodes?: string[] }>('/auth/mfa/verify', {
    user_id: userId,
    code: data.code,
  });
  return response.data;
}

export async function disableMfa(data: MfaVerifyRequest): Promise<void> {
  await api.post('/auth/mfa/disable', data);
}

export async function logoutApi(): Promise<void> {
  const { useAuthStore } = await import('@/stores/authStore');
  const refreshToken = useAuthStore.getState().tokens?.refreshToken;
  if (refreshToken) {
    await api.post('/auth/logout', { refresh_token: refreshToken });
  }
}
